import { useEffect, useRef } from "react";
import { Download, Sparkles, Copy, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { download } from "@/lib/api";

const Chips = ({ title, items, onOpen, testid }) => items?.length ? (
  <div className="mb-2" data-testid={testid}><div className="text-[10px] uppercase tracking-wider text-[#5A6B87] mb-1">{title}</div>
    <div className="flex flex-wrap gap-1">{items.map((p) => <button key={p} onClick={() => onOpen(p)} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[#F3EFFA] text-[#48217A] hover:underline">{p}</button>)}</div></div>
) : null;

function Outline({ file, jump }) {
  return (
    <div className="text-xs space-y-3" data-testid="dev-file-outline">
      {file.routes.length > 0 && <div><div className="text-[10px] uppercase tracking-wider text-[#5A6B87] mb-1">API endpoints</div>
        {file.routes.map((r) => <button key={r.line + r.path} onClick={() => jump(r.line)} className="block w-full text-left font-mono truncate hover:text-[#48217A]"><b className="text-[#2E9B65]">{r.method}</b> {r.path}</button>)}</div>}
      <div><div className="text-[10px] uppercase tracking-wider text-[#5A6B87] mb-1">Functions & components</div>
        {file.symbols.map((s) => <button key={s.line + s.name} onClick={() => jump(s.line)} className="block w-full text-left font-mono truncate hover:text-[#48217A]"><span className="text-[#5A6B87]">{s.line}</span> {s.name}</button>)}</div>
    </div>
  );
}

export function CodeViewer({ file, line, onOpen, onAsk, sectionLabel }) {
  const box = useRef(null);
  const jump = (n) => { const el = box.current?.querySelector(`[data-line="${n}"]`); if (el) { el.scrollIntoView({ block: "center" }); el.classList.add("bg-amber-100"); setTimeout(() => el.classList.remove("bg-amber-100"), 1600); } };
  useEffect(() => { if (line) { setTimeout(() => jump(line), 50); } }, [file.path, line]);
  const rows = file.content.split("\n");
  return (
    <div data-testid="dev-code-viewer">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FileCode2 className="h-5 w-5 text-[#48217A]" /><h2 className="font-mono text-sm font-semibold break-all" data-testid="dev-file-path">{file.path}</h2>
        <span className="text-xs text-[#5A6B87]">{file.lines} lines · {sectionLabel}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(file.content); toast.success("Copied"); }} data-testid="dev-copy-file"><Copy className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => download(`/dev/download?path=${encodeURIComponent(file.path)}`, file.path.split("/").pop())} data-testid="dev-download-file"><Download className="h-4 w-4 mr-1" />File</Button>
          <Button size="sm" className="bg-[#48217A]" onClick={() => onAsk(file.path)} data-testid="dev-ask-file"><Sparkles className="h-4 w-4 mr-1" />Ask AI</Button>
        </div>
      </div>
      <div className="grid xl:grid-cols-[1fr_220px] gap-4">
        <div ref={box} className="rounded-xl border border-[#1E2A40] overflow-auto max-h-[70vh] !bg-[#0F1626] text-[#E6EAF2] text-[12px] font-mono leading-5" data-testid="dev-code-lines">
          {rows.map((l, i) => <div key={i} data-line={i + 1} className="flex transition-colors"><span className="select-none text-right w-12 shrink-0 pr-3 text-[#5A6B87] sticky left-0 bg-[#0F1626]">{i + 1}</span><pre className="whitespace-pre pr-4">{l || " "}</pre></div>)}
        </div>
        <div className="space-y-3">
          <Chips title="Imports" items={file.imports} onOpen={onOpen} testid="dev-file-imports" />
          <Chips title="Used by" items={file.imported_by} onOpen={onOpen} testid="dev-file-imported-by" />
          <Chips title="Calls backend in" items={file.api_files} onOpen={onOpen} testid="dev-file-api" />
          <Outline file={file} jump={jump} />
        </div>
      </div>
    </div>
  );
}
